import { useLayoutEffect, useRef, useState } from 'react';

import { useObserveContainerSize } from '../../../../../core/hooks/useObserveContainerSize.js';
import { SMALL_CHART_SIZE } from '../../../constants.js';

/**
 * Hook responsible to calculate the proper size to allocate to the plot area
 */
export const useComputedSegmentSize = () => {
  const [segmentSize, setSegmentSize] = useState<number>(0);

  const labelRef = useRef<HTMLDivElement | null>(null);
  const valueLabelRef = useRef<HTMLDivElement | null>(null);
  const minLabelRef = useRef<HTMLDivElement | null>(null);
  const maxLabelRef = useRef<HTMLDivElement | null>(null);
  const legendRef = useRef<HTMLDivElement | null>(null);

  const [{ wrapperHeight, height }, containerRef] = useObserveContainerSize();

  const getGridHeightSizes = () => ({
    labelArea: labelRef?.current?.clientHeight ?? 0,
    valueLabelArea: valueLabelRef?.current?.clientHeight ?? 0,
    minLabelArea: minLabelRef?.current?.clientHeight ?? 0,
    maxLabelArea: maxLabelRef?.current?.clientHeight ?? 0,
    legendArea: legendRef?.current?.clientHeight ?? 0,
  });

  useLayoutEffect(() => {
    const {
      labelArea,
      valueLabelArea,
      minLabelArea,
      maxLabelArea,
      legendArea,
    } = getGridHeightSizes();
    const firstRowHeight = Math.max(labelArea, valueLabelArea);
    const secondRowHeight = Math.max(minLabelArea, maxLabelArea);

    const chartHeight = Math.min(
      height,
      wrapperHeight - firstRowHeight - secondRowHeight - legendArea,
    );
    const plotArea = Math.max(SMALL_CHART_SIZE, chartHeight);

    setSegmentSize(plotArea);
  }, [
    height,
    wrapperHeight,
    valueLabelRef,
    minLabelRef,
    maxLabelRef,
    legendRef,
    labelRef,
  ]);

  return {
    segmentSize,
    labelRef,
    valueLabelRef,
    minLabelRef,
    maxLabelRef,
    plotAreaRef: containerRef,
    legendRef,
  };
};
